import React, { useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, Col, Form, Input, InputNumber, Modal, Row, Segmented, Select, Space, Tag, Tooltip, Typography } from 'antd';
import { CopyOutlined, ReloadOutlined, ThunderboltOutlined } from '@ant-design/icons';
import {
  buildPitchText,
  EMPTY_PITCH_FORM,
  hasUnavailableSimilarwebData,
  PitchCountry,
  PitchFormValues,
  PitchTrafficSource,
  PitchVolumeMetric,
  prefillFromInventory,
  readInventoryGeoText,
  readPublisherTerms,
} from '../utils/pitch';
import { copyTextToClipboard } from '../utils/clipboard';
import { PitchPriceField } from './PitchPriceField';
import { inventoryStatusColor } from './CrmPanel';
import { useAdvertiserSearch, useCreateInventoryPitch } from '../../../hooks/useLeadOps';

const { TextArea } = Input;
const { Text } = Typography;

type InventoryMatchLike = {
  item_id?: string;
  name?: string | null;
  domain?: string | null;
  columns_preview?: Array<{ id?: string; title?: string; text?: string }>;
};

type InventoryPitchModalProps = {
  open: boolean;
  match: InventoryMatchLike | null;
  domain: string;
  domains?: string[];
  onDomainChange?: (domain: string) => void;
  desktopShare?: number | null;
  mobileShare?: number | null;
  topCountries: PitchCountry[];
  trafficSources: PitchTrafficSource[];
  swLoading?: boolean;
  trafficSourcesLoading?: boolean;
  trafficSourcesError?: string;
  onClose: () => void;
};

export function InventoryPitchModal({
  open,
  match,
  domain,
  domains = [],
  onDomainChange,
  desktopShare,
  mobileShare,
  topCountries,
  trafficSources,
  swLoading = false,
  trafficSourcesLoading = false,
  trafficSourcesError,
  onClose,
}: InventoryPitchModalProps): React.ReactElement {
  const { message } = App.useApp();
  const [advertiserId, setAdvertiserId] = useState<string | null>(null);
  const [advertiserName, setAdvertiserName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [formValues, setFormValues] = useState<PitchFormValues>(EMPTY_PITCH_FORM);
  const [pitchText, setPitchText] = useState('');
  const [isManuallyEdited, setIsManuallyEdited] = useState(false);
  const [copyingCrmGeo, setCopyingCrmGeo] = useState(false);

  const createPitch = useCreateInventoryPitch();
  const advertiserQuery = useAdvertiserSearch(debouncedTerm);
  const advertiserOptions = advertiserQuery.data?.data?.items || [];

  const inventoryName = (match?.name || '').trim();
  const crmGeoText = readInventoryGeoText(match);
  const publisherTerms = useMemo(() => (match ? readPublisherTerms(match) : null), [match]);
  const showDomainPicker = domains.length > 1;
  const dealRateLabel = useMemo(() => {
    if (!publisherTerms) return '';
    const dealType = publisherTerms.dealType.trim();
    const rateRaw = publisherTerms.rate.trim();
    const rate = rateRaw
      ? (rateRaw.startsWith('$') ? rateRaw : `$${rateRaw}`)
      : '';
    return [dealType, rate].filter(Boolean).join(' · ');
  }, [publisherTerms]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedTerm(searchTerm), 300);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  useEffect(() => {
    if (!open) return;
    setAdvertiserId(null);
    setAdvertiserName('');
    setSearchTerm('');
    setDebouncedTerm('');
    setIsManuallyEdited(false);
    setFormValues(match ? prefillFromInventory(match) : EMPTY_PITCH_FORM);
  }, [open, match]);

  const similarwebData = useMemo(() => ({
    domain,
    desktopShare,
    mobileShare,
    topCountries,
    trafficSources,
  }), [desktopShare, domain, mobileShare, topCountries, trafficSources]);

  const generatedPitch = useMemo(
    () => buildPitchText(formValues, similarwebData),
    [formValues, similarwebData],
  );
  const hasMissingData = hasUnavailableSimilarwebData(similarwebData);

  useEffect(() => {
    if (!isManuallyEdited) setPitchText(generatedPitch);
  }, [generatedPitch, isManuallyEdited]);

  const updateForm = <K extends keyof PitchFormValues>(key: K, value: PitchFormValues[K]) => {
    setFormValues((current) => ({ ...current, [key]: value }));
  };

  const regenerate = () => {
    setPitchText(generatedPitch);
    setIsManuallyEdited(false);
  };

  const handleCopyCrmGeo = async () => {
    if (!crmGeoText) return;
    setCopyingCrmGeo(true);
    try {
      await copyTextToClipboard(crmGeoText);
      message.success('CRM GEO copied');
    } catch {
      message.error('Could not copy CRM GEO');
    } finally {
      setCopyingCrmGeo(false);
    }
  };

  const dealNamePreview = advertiserName && inventoryName
    ? `${advertiserName} - ${inventoryName}`
    : '';

  const canSubmit = Boolean(advertiserId) && Boolean(match?.item_id) && formValues.volumeMetric !== null;

  const handleCopyAndCreate = async () => {
    if (!advertiserId || !match?.item_id) {
      message.warning('Choose an advertiser first');
      return;
    }
    if (formValues.volumeMetric === null) {
      message.warning('Choose Clicks or Impressions');
      return;
    }
    let copied = false;
    try {
      await copyTextToClipboard(pitchText);
      copied = true;
    } catch {
      message.warning('Could not copy pitch yet; deal will still be created');
    }
    try {
      const response = await createPitch.mutateAsync({
        inventory_item_id: String(match.item_id),
        inventory_item_name: inventoryName,
        advertiser_id: advertiserId,
        advertiser_name: advertiserName,
        expected_daily_volume: formValues.expectedDailyVolume,
      });
      const data = response?.data;
      if (data?.duplicate_warning) {
        message.warning('A similar deal already exists for this inventory and advertiser. Created anyway.');
      }
      const url = data?.url;
      if (url) {
        message.success(
          <span>
            {copied ? 'Deal created & pitch copied. ' : 'Deal created. '}
            <a href={url} target="_blank" rel="noopener noreferrer">Open deal</a>
          </span>,
        );
      } else {
        message.success(copied ? 'Deal created & pitch copied' : 'Deal created');
      }
      onClose();
    } catch {
      message.error('Could not create the advertiser deal');
    }
  };

  return (
    <Modal
      className="pitch-composer-modal"
      title="Pitch from Inventory"
      open={open}
      onCancel={onClose}
      width={760}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      footer={(
        <Space wrap>
          <Button onClick={onClose}>Cancel</Button>
          <Button icon={<ReloadOutlined />} onClick={regenerate}>Regenerate</Button>
          <Tooltip title={!canSubmit ? 'Choose an advertiser and Clicks/Impressions first' : undefined}>
            <span>
              <Button
                type="primary"
                icon={<CopyOutlined />}
                onClick={handleCopyAndCreate}
                loading={createPitch.isPending}
                disabled={!canSubmit}
              >
                Copy &amp; Create Deal
              </Button>
            </span>
          </Tooltip>
        </Space>
      )}
    >
      <Form layout="vertical">
        <Form.Item label="Inventory item">
          <Input value={inventoryName} disabled />
        </Form.Item>
        {showDomainPicker && (
          <Form.Item
            label="Pitch for site"
            htmlFor="inv-pitch-domain"
            extra="This inventory covers multiple sites — pick one for Similarweb metrics."
          >
            <Select
              id="inv-pitch-domain"
              showSearch
              value={domain || undefined}
              options={domains.map((item) => ({ value: item, label: item }))}
              onChange={(value) => onDomainChange?.(value)}
              optionFilterProp="label"
              style={{ width: '100%' }}
            />
          </Form.Item>
        )}
        <Form.Item label="Advertiser" required htmlFor="pitch-advertiser">
          <Select
            id="pitch-advertiser"
            showSearch
            allowClear
            placeholder="Search advertisers in CRM…"
            filterOption={false}
            notFoundContent={advertiserQuery.isFetching ? 'Searching…' : (debouncedTerm.length >= 2 ? 'No advertisers found' : 'Type at least 2 characters')}
            onSearch={setSearchTerm}
            onChange={(value, option) => {
              setAdvertiserId(value || null);
              const label = Array.isArray(option) ? '' : (option?.label as string) || '';
              setAdvertiserName(label);
            }}
            value={advertiserId}
            options={advertiserOptions.map((item) => ({ value: item.id, label: item.name }))}
            loading={advertiserQuery.isFetching}
            style={{ width: '100%' }}
          />
        </Form.Item>
        {dealNamePreview && (
          <Alert
            type="info"
            showIcon
            icon={<ThunderboltOutlined />}
            message={<span>Deal name: <strong>{dealNamePreview}</strong></span>}
            style={{ marginBottom: 16 }}
          />
        )}

        <Row gutter={16}>
          <Col xs={24} md={10}>
            <Form.Item label="Spot name" htmlFor="inv-pitch-spot-name">
              <Input
                id="inv-pitch-spot-name"
                value={formValues.spotName}
                placeholder="Natives"
                onChange={(event) => updateForm('spotName', event.target.value)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Duration" htmlFor="inv-pitch-duration">
              <Input
                id="inv-pitch-duration"
                value={formValues.duration}
                placeholder="1 month"
                onChange={(event) => updateForm('duration', event.target.value)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Publisher terms">
              <div className="leadops-pitch-terms-box">
                {publisherTerms?.status && (
                  <Tag color={inventoryStatusColor(publisherTerms.status)} style={{ margin: 0 }}>
                    {publisherTerms.status}
                  </Tag>
                )}
                {dealRateLabel && (
                  <Tag color="geekblue" style={{ margin: 0 }}>{dealRateLabel}</Tag>
                )}
                {publisherTerms && !publisherTerms.status && !dealRateLabel && (
                  <Text type="secondary">—</Text>
                )}
              </div>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Spot description" htmlFor="inv-pitch-description">
          <TextArea
            id="inv-pitch-description"
            value={formValues.spotDescription}
            placeholder="#3 on desktop and mobile, on ALL PAGES"
            autoSize={{ minRows: 2, maxRows: 4 }}
            onChange={(event) => updateForm('spotDescription', event.target.value)}
          />
        </Form.Item>
        <Row gutter={16} className="pitch-commercial-fields">
          <Col xs={24} md={12}>
            <Form.Item label="Volume metric" className="pitch-volume-metric-field">
              <Segmented<PitchVolumeMetric | ''>
                block
                className="pitch-volume-metric"
                options={[
                  { label: 'Clicks', value: 'clicks' },
                  { label: 'Impressions', value: 'impressions' },
                ]}
                value={formValues.volumeMetric ?? ''}
                onChange={(value) => updateForm('volumeMetric', value || null)}
              />
            </Form.Item>
            <Form.Item
              label={`Expected daily ${formValues.volumeMetric || 'volume'}`}
              htmlFor="inv-pitch-volume"
            >
              <InputNumber
                id="inv-pitch-volume"
                value={formValues.expectedDailyVolume}
                min={0}
                precision={0}
                placeholder="4250"
                onChange={(value) => updateForm('expectedDailyVolume', value)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <PitchPriceField
              id="inv-pitch-price"
              model={formValues.priceModel}
              value={formValues.priceUsd}
              onModelChange={(value) => updateForm('priceModel', value)}
              onValueChange={(value) => updateForm('priceUsd', value)}
            />
          </Col>
        </Row>

        {swLoading && (
          <Alert type="info" showIcon message="Loading Similarweb summary…" style={{ marginBottom: 16 }} />
        )}
        {trafficSourcesLoading && (
          <Alert type="info" showIcon message="Loading Traffic Sources…" style={{ marginBottom: 16 }} />
        )}
        {!swLoading && !trafficSourcesLoading && trafficSourcesError && (
          <Alert type="warning" showIcon message="Traffic Sources unavailable. You can edit the draft manually." style={{ marginBottom: 16 }} />
        )}
        {!swLoading && !trafficSourcesLoading && !trafficSourcesError && hasMissingData && (
          <Alert type="warning" showIcon message="Some Similarweb fields are unavailable and marked in the draft." style={{ marginBottom: 16 }} />
        )}

        <Form.Item style={{ marginBottom: 0 }}>
          <div className="leadops-pitch-editor">
            <div className="leadops-pitch-editor-heading">
              <label htmlFor="inv-pitch-text">Pitch text</label>
              <Tooltip title={crmGeoText
                ? <span className="leadops-crm-geo-tooltip">{crmGeoText}</span>
                : 'GEO is empty for this Inventory item'}>
                <span>
                  <Button
                    type="text"
                    size="small"
                    icon={<span className="monday-mark" aria-hidden="true"><span /><span /><span /></span>}
                    onClick={handleCopyCrmGeo}
                    loading={copyingCrmGeo}
                    disabled={!crmGeoText}
                  >
                    Copy CRM GEO
                  </Button>
                </span>
              </Tooltip>
            </div>
            <TextArea
              id="inv-pitch-text"
              value={pitchText}
              rows={14}
              onChange={(event) => {
                setPitchText(event.target.value);
                setIsManuallyEdited(event.target.value !== generatedPitch);
              }}
            />
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
}
